import React from "react";
import PropTypes from "prop-types";
import { Modal } from "shared/Modal";
import Button from "shared/Button";
import SearchInput from "shared/@forms/SearchInput";
import Loader from "shared/Loader";
import AlertNotice from "shared/AlertNotice";
import { products as productsApi, errorMessage } from "shared/api";
import { formatCents } from "shared/@utils/money";
import styles from "./BundleProductPicker.module.css";

/**
 * Picks products out of the existing catalog to put into a bundle.
 *
 * Bundles reference products that already exist in the Store — NetSuite owns
 * SKU creation, so this only ever selects, never creates.
 */
export default function BundleProductPicker({ isOpen, onClose, onAdd }) {
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [chosen, setChosen] = React.useState({});
  const reqIdRef = React.useRef(0);

  React.useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setResults([]);
      setError(null);
      setChosen({});
    }
  }, [isOpen]);

  // Debounced catalog search. Bundles themselves are excluded — nesting a
  // bundle inside a bundle has no defined pricing behaviour.
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const reqId = reqIdRef.current + 1;
    reqIdRef.current = reqId;
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      productsApi
        .list({ search: query, pageSize: 25 })
        .then((res) => {
          if (reqIdRef.current !== reqId) return;
          const list = (res && (res.items || res.products || res)) || [];
          setResults(
            (Array.isArray(list) ? list : []).filter((p) => !p.isBundle)
          );
        })
        .catch((err) => {
          if (reqIdRef.current !== reqId) return;
          setError(errorMessage(err, "Couldn't load the catalog."));
        })
        .finally(() => {
          if (reqIdRef.current === reqId) setLoading(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [isOpen, query]);

  const toggle = (product) => {
    setChosen((prev) => {
      const next = { ...prev };
      if (next[product.id]) delete next[product.id];
      else next[product.id] = product;
      return next;
    });
  };

  const handleAdd = () => {
    onAdd(
      Object.values(chosen).map((p) => {
        // Same precedence the catalog list uses: NetSuite's live figures with
        // any admin override applied, falling back to the override store alone.
        const price = p.effectivePricing || p.pricing || {};
        return {
          productId: p.id,
          sku: p.sku || p.netsuiteSku || "",
          title: p.name || p.netsuiteTitle || p.sku || "",
          thumbnailUrl:
            p.effectiveThumbnailUrl ||
            (p.thumbnailUrl && (p.thumbnailUrl.url || p.thumbnailUrl)) ||
            null,
          retailCents: price.retailCents ?? null,
          premierMemberCents: price.premierMemberCents ?? null,
          bundledItemCents: null,
        };
      })
    );
  };

  const chosenCount = Object.keys(chosen).length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Select Products">
      <div className={styles.body}>
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e && e.target ? e.target.value : e || "")}
          placeholder="Search by product name or SKU"
        />
        {error && <AlertNotice type="error">{error}</AlertNotice>}
        {loading && <Loader />}
        {!loading && !error && results.length === 0 && (
          <p className={styles.empty}>No products match that search.</p>
        )}
        <ul className={styles.results}>
          {results.map((product) => (
            <li key={product.id}>
              <label className={styles.result}>
                <input
                  type="checkbox"
                  checked={Boolean(chosen[product.id])}
                  onChange={() => toggle(product)}
                />
                <span className={styles.resultName}>
                  {product.name || product.netsuiteTitle || product.sku}
                </span>
                <span className={styles.resultMeta}>
                  {product.sku || product.netsuiteSku
                    ? `SKU: ${product.sku || product.netsuiteSku}`
                    : ""}
                  {(() => {
                    const price =
                      product.effectivePricing || product.pricing || {};
                    return price.retailCents !== undefined &&
                      price.retailCents !== null
                      ? ` | ${formatCents(price.retailCents)}`
                      : "";
                  })()}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.actions}>
        <Button onClick={onClose} title="Close without changing the bundle.">
          Cancel
        </Button>
        <Button
          primary
          disabled={chosenCount === 0}
          onClick={handleAdd}
          title="Add the selected products to this bundle."
        >
          {chosenCount > 0 ? `Add ${chosenCount}` : "Add"}
        </Button>
      </div>
    </Modal>
  );
}

BundleProductPicker.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onAdd: PropTypes.func,
};

BundleProductPicker.defaultProps = {
  isOpen: false,
  onClose: () => {},
  onAdd: () => {},
};
